import { Location } from '@angular/common';
import { Component, OnInit, AfterViewInit, Inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { AppWindow } from '../../AppWindow';
import { AuthorizationService, IPrincipal } from '../../authorization/authorization.service';
import { FeatureFlagService } from '../../core/feature-flags/feature-flag.service';
import { PortalFormControl } from '../../shared/classes/portal-form-control';
import { SalesLeadService } from '../../shared/modules/sales-lead/sales-lead.service';
import { IUserInfo, UserDataAccessService } from '../../user-management/user-data-access.service';
import { ContactSalesModalService } from '../contact-sales-modal/contact-sales-modal-service';
import { ContactSalesService } from './contact-sales.service';
import { IEloquaSalesLead } from '../../shared/modules/sales-lead/eloqua-sales-lead-data-access.service';
import { EloquaSalesLeadService } from '../../shared/modules/sales-lead/eloqua-sales-lead.service';

export class State {
  name: string | undefined;
  code: string | undefined;
}

export class Country {
  name: string | undefined;
  cc: string | undefined;
  ccc: string | undefined;
  states: State[] | undefined;
}

export class ContactSalesFormModel {
  cmnowreasons: string[] | undefined;
  aointerests: string[] | undefined;
  industries: string[] | undefined;
  geo: Country[] | undefined;
}

export class SalesFormAngularticsEvent {
  companyName?: string;
  jobTitle?: string;
  country?: Country;
  industry?: string;
  areaOfInterest?: string;
  contactMeNow?: string;
  stateOrProvince?: string;
}

interface IContactSalesForm {
  comment: PortalFormControl<string | null>;
  reason: PortalFormControl<string | null>;
  areaOfInterest: PortalFormControl<string | null>;
  email: PortalFormControl<string | null>;
  firstName: PortalFormControl<string | null>;
  lastName: PortalFormControl<string | null>;
  companyName: PortalFormControl<string | null>;
  industry: PortalFormControl<string | null>;
  jobTitle: PortalFormControl<string | null>;
  country: PortalFormControl<Country>;
  stateOrProvince: PortalFormControl<string | null>;
  phone: PortalFormControl<string | null>;
  recaptchaReactive: PortalFormControl<string | null>;
}

@Component({
  selector: 'app-contact-sales',
  templateUrl: './contact-sales.component.html',
  styleUrls: ['./contact-sales.component.scss'],
})
export class ContactSalesComponent implements OnInit, AfterViewInit {
  private isReCaptchaTokenVerified: boolean = false;
  public GOOGLE_RECAPTCHA_SITE_KEY: string;
  public contactSalesFormGroup!: FormGroup<IContactSalesForm>;
  public recaptchaReactive: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: null },
    {
      validators: [Validators.required],
    },
  );
  public comment: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '' },
    {
      validators: [Validators.required],
    },
  );
  public reason: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '' },
    {
      validators: [Validators.required],
    },
  );
  public areaOfInterest: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '' },
    {
      validators: [Validators.required],
    },
  );
  public email: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '', disabled: true },
    {},
  );
  public firstName: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '', disabled: true },
    {},
  );
  public lastName: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '', disabled: true },
    {},
  );
  public companyName: PortalFormControl<string | null> = new PortalFormControl<string | null>(
    { value: '', disabled: true },
    {},
  );
  public industry: PortalFormControl<string | null> = new PortalFormControl<string | null>(null, {
    validators: [Validators.required],
  });
  public jobTitle: PortalFormControl<string | null> = new PortalFormControl<string | null>(null, {
    validators: [Validators.required],
  });
  public country: PortalFormControl<Country> = new PortalFormControl<Country>(null, {
    validators: [Validators.required],
  });
  public stateOrProvince: PortalFormControl<string | null> = new PortalFormControl<string | null>(null, {
    validators: [Validators.required],
  });
  public phonePattern: string = '';
  public industries$: Subject<string[]> = new Subject<string[]>();
  public countries$: Subject<Country[]> = new Subject<Country[]>();
  public phone: PortalFormControl<string | null> = new PortalFormControl<string | null>(null, {
    validators: [Validators.required, Validators.pattern(this.phonePattern)],
  });
  public contactMeNowReasons$: Subject<string[]> = new Subject<string[]>();
  public areasOfInterest$: Subject<string[]> = new Subject<string[]>();
  public angularTicsRequest: SalesFormAngularticsEvent | undefined;
  private _salesFormResponse: ContactSalesFormModel | undefined;
  private _busy$: Subject<boolean> = new Subject<boolean>();
  public busy$: Observable<boolean> = this._busy$.pipe(shareReplay(1));
  private _saveBusy$: Subject<boolean> = new Subject<boolean>();
  public saveBusy$: Observable<boolean> = this._saveBusy$.pipe(shareReplay(1));
  private toastrOptions: IndividualConfig = {
    closeButton: true,
    timeOut: 6000,
  } as IndividualConfig;

  constructor(
    private _contactSalesService: ContactSalesService,
    private _authorization: AuthorizationService,
    private _salesLeadService: SalesLeadService,
    private _eloquaSalesLeadService: EloquaSalesLeadService,
    private _contactSalesModalService: ContactSalesModalService,
    private _location: Location,
    private _toastr: ToastrService,
    private _userDataService: UserDataAccessService,
    private _featureFlagService: FeatureFlagService,
    @Inject('window') private _appWindow: AppWindow,
  ) {
    this.GOOGLE_RECAPTCHA_SITE_KEY = this._appWindow.portalConfig.googleReCaptchaSiteKey ?? ''; //there is no valid source of the key in this._window.portalConfig.GOOGLE_RECAPTCHA_SITE_KEY
  }

  public get isDisabled(): boolean {
    return !(this.contactSalesFormGroup.valid && this.isReCaptchaTokenVerified);
  }

  public onCountryChange(): void {
    const c: Country = this.country.value;
    if (c.cc) {
      this._validatePhonePattern(c.cc);
    }
  }

  public onCancel(): void {
    this._location.back();
  }

  public async onSave(): Promise<void> {
    if (this.contactSalesFormGroup.invalid) {
      return;
    }
    const contactSalesData: IEloquaSalesLead = {
      Comments: this.contactSalesFormGroup.get('comment')?.value,
      ImInquiringAbout: this.contactSalesFormGroup.get('areaOfInterest')?.value,
      Reason: this.contactSalesFormGroup.get('reason')?.value,
      emailAddress: this.contactSalesFormGroup.get('email')?.value,
      firstName: this.contactSalesFormGroup.get('firstName')?.value,
      lastName: this.contactSalesFormGroup.get('lastName')?.value,
      Industry: this.contactSalesFormGroup.get('industry')?.value,
      Job_Title1: this.contactSalesFormGroup.get('jobTitle')?.value,
      C_Country: this.contactSalesFormGroup.get('country')?.value.cc,
      C_State_Prov: this.contactSalesFormGroup.get('stateOrProvince')?.value,
      BusPhone: this.contactSalesFormGroup.get('phone')?.value,
      company: this.contactSalesFormGroup.get('companyName')?.value,
      ImInterestedIn: 'Sales',
    };
    this._saveBusy$.next(true);
    try {
      await this._eloquaSalesLeadService.contactSales(contactSalesData);
      // Display Confirmation Modal
      this._openModal();
    } catch (error: any) {
      this._toastr.error(error, 'Failed to contact sales', this.toastrOptions);
    } finally {
      this._saveBusy$.next(false);
    }
  }

  ngAfterViewInit() {
    this._appWindow.scrollTo(0, 0);
  }

  ngOnInit(): void {
    this.contactSalesFormGroup = new FormGroup<IContactSalesForm>({
      comment: this.comment,
      reason: this.reason,
      areaOfInterest: this.areaOfInterest,
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      companyName: this.companyName,
      industry: this.industry,
      jobTitle: this.jobTitle,
      country: this.country,
      stateOrProvince: this.stateOrProvince,
      phone: this.phone,
      recaptchaReactive: this.recaptchaReactive,
    });
    this.contactSalesFormGroup.valueChanges.subscribe((result) => {
      delete result.phone;
      this.angularTicsRequest = result;
      if (this.country && this.country.value) {
        this.angularTicsRequest.country = this.country.value;
      }
      if (this.stateOrProvince && this.stateOrProvince.value) {
        this.angularTicsRequest.stateOrProvince = this.stateOrProvince.value;
      }
    });
    this.comment.setValue('');
    this.jobTitle.setValue('');
    this.industry.setValue('');
    this.areaOfInterest.setValue('');
    this.reason.setValue('');
    this._busy$.next(true);
    this._saveBusy$.next(true);
    const obs1 = this._contactSalesService.getSalesFormValues();
    obs1.subscribe((response) => {
      this._salesFormResponse = response;
      this.industries$.next(response.industries!);
      this.countries$.next(response.geo!);
      this.country.setValue(response.geo![0]);
      this.areasOfInterest$.next(response.aointerests!);
      this.contactMeNowReasons$.next(response.cmnowreasons!);
    });
    this._authorization.principal$.subscribe((user: IPrincipal) => {
      if (user.identity.isAuthenticated) {
        this.firstName.setValue(user.identity.givenName);
        this.email.setValue(user.identity.email);
        this.lastName.setValue(user.identity.familyName);
        if (user.identity.email) {
          const obs2 = this._userDataService.getUserInfo(user.identity.email);
          obs2.subscribe((subscription) => {
            this._setCompanyName(subscription);
            this._busy$.next(false);
            this._saveBusy$.next(false);
          });
          forkJoin([obs1, obs2]).subscribe((_result) => this._busy$.next(false));
        } else {
          throw new Error('Could not retrieve user email information');
        }
      } else {
        this.firstName.enable();
        this.lastName.enable();
        this.companyName.enable();
        this.email.enable();
      }
    });
    this._featureFlagService.getFeatures().subscribe((features) => {
      this.isReCaptchaTokenVerified = !features.google_recaptcha_v2?.isEnabled;
    });
  }

  private _validatePhonePattern(countryCode: string): string {
    if (countryCode === 'US' || countryCode === 'CA') {
      this.phonePattern = '([+]?\\d{1,2}[.-\\s]?)?(\\d{3}[.-]?){2}\\d{4}';
    } else {
      this.phonePattern = '^[^a-zA-Z]+$';
    }
    return this.phonePattern;
  }

  private _setCompanyName(subscriptions: IUserInfo): void {
    let companyName = '';
    if (
      subscriptions.entitlements &&
      subscriptions.entitlements.subscriptionMemberships &&
      subscriptions.entitlements.subscriptionMemberships.length > 0
    ) {
      subscriptions.entitlements.subscriptionMemberships.forEach((subscription) => {
        if (subscription.subscription && subscription.subscription.companyName) {
          companyName = subscription.subscription.companyName;
        }
      });
    }
    this.companyName.setValue(companyName);
    if (companyName === '') {
      this.companyName.enable();
    }
  }

  private _openModal(): Observable<NgbModalRef> {
    return this._contactSalesModalService.open();
  }

  public onCaptchaResolved(reCaptachaToken: string) {
    this._contactSalesService.verifyGoogleReCaptcha(reCaptachaToken).subscribe(
      (reCaptachaResponse) => {
        this.isReCaptchaTokenVerified = reCaptachaResponse.success;
      },
      (error) => {
        this.onCaptchaError(error);
      },
    );
  }

  public onCaptchaError(error: any) {
    this.isReCaptchaTokenVerified = false;
    console.warn(`reCAPTCHA error encountered `, error);
  }
}
